<script lang="ts">
import type { CalcCard, FadedCard } from '$types/card';
import RichText from './RichText.svelte';

interface Props {
  card: CalcCard | FadedCard;
  revealed: boolean;
  fadeLevel: number;
}

const { card, revealed, fadeLevel }: Props = $props();
const steps = $derived(card.calc?.steps ?? []);
const given = $derived(card.calc?.given ?? []);

function isFaded(idx: number): boolean {
  if (revealed) return false;
  let fadable = 0;
  for (let i = 0; i <= idx; i++) {
    if (steps[i]?.can_fade) fadable++;
  }
  return steps[idx]?.can_fade === true && fadable <= fadeLevel;
}

function blocks(text: string): string {
  return '▓'.repeat(Math.min(Math.max(8, text.length), 32));
}
</script>

<div class="card-calc">
  <div class="card-text"><RichText content={card.front} /></div>

  {#if given.length}
    <div class="calc-given">
      <div class="given-label">geg.</div>
      <ul>{#each given as g}<li><RichText content={g} /></li>{/each}</ul>
    </div>
  {/if}

  {#if steps.length}
    <div class="calc-steps">
      <div class="given-label">{revealed ? 'lösung' : 'schritte'}</div>
      <ol>
        {#each steps as step, i}
          <li class={revealed && !isFaded(i) ? 'step-on' : 'step-off'}>
            {#if revealed && !isFaded(i)}
              <RichText content={step.text} />
            {:else}
              <span class="redacted">{blocks(step.text)}</span>
            {/if}
          </li>
        {/each}
      </ol>
    </div>
  {/if}

  {#if revealed && card.back}
    <div class="card-back">
      <div class="back-label">↳ check</div>
      <div class="back-body"><RichText content={card.back} /></div>
    </div>
  {/if}
</div>

<style>
  .card-text {
    font-family: var(--font-body), 'Inter', system-ui, sans-serif;
    font-size: 20px;
    line-height: 1.35;
    color: var(--ink);
    font-weight: 500;
    letter-spacing: -0.01em;
  }
  @media (min-width: 600px) { .card-text { font-size: 24px; } }

  .card-calc { display: flex; flex-direction: column; gap: 10px; }
  .calc-given, .calc-steps { font-family: var(--font-mono); font-size: 13px; }
  .given-label {
    font-size: 9px;
    letter-spacing: 0.14em;
    color: var(--muted);
    text-transform: uppercase;
    margin-bottom: 4px;
    border-left: 3px solid var(--accent);
    padding-left: 6px;
  }
  .calc-given ul, .calc-steps ol { margin: 0; padding-left: 16px; }
  .calc-given li, .calc-steps li { margin: 2px 0; color: var(--ink-2); }
  /* RichText-Wrapper in Listenpunkten inline halten, sonst bricht die Marker-Zeile */
  .calc-given li :global(.richtext), .calc-steps li :global(.richtext) { display: inline-block; vertical-align: top; }
  .step-off { list-style: '□  '; color: var(--muted); }
  .step-on { list-style: '▸  '; color: var(--ink); }
  .redacted {
    color: var(--ink);
    background: var(--ink);
    user-select: none;
    font-family: var(--font-mono);
  }

  .card-back {
    margin-top: 18px;
    padding: 12px;
    background: var(--paper-2);
    border-left: 4px solid var(--accent);
    position: relative;
  }
  .back-label {
    font-size: 10px;
    letter-spacing: 0.12em;
    color: var(--accent-2);
    text-transform: uppercase;
    font-weight: 700;
    margin-bottom: 6px;
  }
  .back-body {
    font-family: var(--font-body), 'Inter';
    font-size: 14px;
    line-height: 1.55;
    color: var(--ink-2);
  }
</style>
